import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getDataQAForInput } from 'components/Inputs/helpers';
import MultiSelect from 'components/Inputs/MultiSelect';
import { CUSTOM_OBJECTS } from 'queries/query-keys';
import CustomObjectsService from 'services/CustomObjectsService';
import { FIELD_TYPES } from 'utility/constants';
import { EMPTY_ARRAY } from 'utility/fieldHelpers';

const SEARCH_PATTERN_FIELD_TYPES = [
  FIELD_TYPES.Text.type,
  FIELD_TYPES.Email.type,
  FIELD_TYPES.PhoneNumber.type,
];

const hideRemoveIcon = ({ value }) => value === 'name';

export const EntitySearchPatternTextSelect = ({
  objectId,
  entitySearchTextPattern,
  onChange,
  validationProps,
}) => {
  const { t } = useTranslation();
  const {
    data: { fields = EMPTY_ARRAY, options = EMPTY_ARRAY } = {},
    isLoading,
  } = useQuery({
    queryKey: [...CUSTOM_OBJECTS.FIELDS_UNRESTRICTED(objectId), 'categorized'],
    queryFn: () =>
      CustomObjectsService.getCategorizedModelFields(objectId, {
        settingsSearch: true,
      }),
    select: (data) => {
      const options = (data?.categorizedFields || []).reduce(
        (acc, { name, fields, id }) => {
          const items = fields
            .reduce((fieldsAcc, { fieldType, name, displayName, order }) => {
              if (SEARCH_PATTERN_FIELD_TYPES.includes(fieldType)) {
                fieldsAcc.push({ value: name, label: displayName, order });
              }
              return fieldsAcc;
            }, [])
            .sort((a, b) => a.order - b.order);

          if (items.length) {
            acc.push({
              key: id,
              label: name,
              options: items,
            });
          }
          return acc;
        },
        []
      );
      return {
        fields: data?.fields || [],
        options: options,
      };
    },
    staleTime: Infinity,
    enabled: Boolean(objectId),
  });

  const fieldsDisplayNameByName = useMemo(
    () =>
      fields.reduce((acc, { name, displayName }) => {
        acc[name] = displayName;
        return acc;
      }, {}),
    [fields]
  );

  const value = useMemo(() => {
    return [
      ...(entitySearchTextPattern || '').matchAll(/{{\s*([\w_]+)\s*}}/g),
    ].map(([, name]) => ({
      value: name,
      label: fieldsDisplayNameByName[name] || name,
    }));
  }, [entitySearchTextPattern, fieldsDisplayNameByName]);

  return (
    <MultiSelect
      label={t('Fields to Include in Search')}
      placeholder={t('Find Fields')}
      options={options}
      isLoading={isLoading}
      value={value}
      onChange={onChange}
      hideRemoveIcon={hideRemoveIcon}
      {...validationProps}
      {...getDataQAForInput('entity_search_text_pattern', 'dropdown')}
    />
  );
};
